import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import SEO from "../../components/SEO";

const ContactUs = () => {
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 5000);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <>
      <SEO 
        title="Contact Us | Get a Quote for Label Printing" 
        description="Contact Sahajink Pvt Ltd for custom label printing solutions. Get a free quote for Pharma, Food, or Cosmetic labels. Located in Surat, India."
        keywords="contact label printer surat, label printing quote, inquire custom tags india, sahajink contact number, label factory address surat"
      />
      <div className="bg-white min-h-screen pt-[80px]">
      {/* Cinematic Hero Section */}
      <section className="relative py-24 md:py-32 bg-[#0a1628] overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_20%_30%,rgba(37,99,235,0.15),transparent_50%)]"></div>
          <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(circle_at_80%_70%,rgba(37,99,235,0.1),transparent_50%)]"></div>
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: "radial-gradient(#fff 1px, transparent 1px)",
              backgroundSize: "40px 40px",
            }}
          ></div>
        </div>

        <div className="max-w-[1200px] mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="inline-block px-5 py-2 bg-blue-600/10 border border-blue-500/20 rounded-full text-blue-400 text-xs font-black uppercase tracking-[0.3em] mb-8">
              Connect With Us
            </div>
            <h1 className="text-5xl md:text-7xl font-black text-white mb-8 tracking-tighter leading-[0.9]">
              Let's Bring Your <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-600">
                Labels to Life.
              </span>
            </h1>
            <p className="text-white/40 max-w-[700px] mx-auto text-lg md:text-xl font-medium leading-relaxed mb-12">
              Whether you need a high-volume industrial run or expert
              consultation on substrates, we're here to help you scale with
              precision.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Contact Grid */}
      <section className="py-24 md:py-32 bg-white relative">
        <div className="max-w-[1200px] mx-auto px-6 grid lg:grid-cols-12 gap-16 lg:gap-24 items-start">
          {/* Left: Contact Info */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="lg:col-span-5 space-y-12"
          >
            <div>
              <h2 className="text-3xl font-black text-gray-900 mb-6 tracking-tight">
                Direct Channels
              </h2>
              <p className="text-gray-500 font-medium leading-relaxed">
                Our technical team is available for deep-dive consultations and
                emergency order support.
              </p>
            </div>

            <div className="space-y-8">
              {[
                {
                  icon: "📍",
                  label: "Surat Headquarters",
                  value:
                    "Sahajink PVT LTD, Industrial Area, Phase II, Surat, Gujarat – 395006",
                  sub: "Regional Manufacturing Hub",
                },
                {
                  icon: "📞",
                  label: "Inquiry Line",
                  value: "+91 63524 24442",
                  sub: "Available Mon-Sat, 9AM-6PM",
                },
                {
                  icon: "✉️",
                  label: "Business Support",
                  value: "connect@sahajink.com",
                  sub: "Avg response: < 2 hours",
                },
              ].map((info, idx) => (
                <motion.div
                  key={idx}
                  variants={itemVariants}
                  className="flex gap-6 group"
                >
                  <div className="w-14 h-14 bg-gray-50 border border-gray-100 rounded-2xl flex items-center justify-center text-2xl transition-all duration-300 group-hover:bg-blue-600 group-hover:text-white group-hover:shadow-[0_15px_30px_rgba(37,99,235,0.2)]">
                    {info.icon}
                  </div>
                  <div>
                    <span className="block text-[10px] font-black text-blue-600 uppercase tracking-widest mb-1">
                      {info.label}
                    </span>
                    <p className="text-gray-900 font-bold text-lg leading-tight mb-1">
                      {info.value}
                    </p>
                    <p className="text-gray-400 text-xs font-semibold">
                      {info.sub}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Subtle Map Placeholder/Illustration */}
            <motion.div
              variants={itemVariants}
              className="relative p-8 bg-gray-900 rounded-[40px] overflow-hidden min-h-[200px] flex flex-col justify-end group shadow-2xl"
            >
              <div className="absolute inset-0 bg-gradient-to-t from-blue-600/50 to-transparent z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative z-20">
                <h3 className="text-white font-black text-xl mb-2">
                  Global Shipping
                </h3>
                <p className="text-white/60 text-sm font-medium">
                  Delivering premium label solutions to over 15+ countries.
                </p>
              </div>
            </motion.div>
          </motion.div>

          {/* Right: Modern Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7"
          >
            <div className="bg-white border border-gray-100 rounded-[48px] p-8 md:p-14 shadow-[0_50px_100px_-20px_rgba(0,0,0,0.06)] relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50/50 rounded-bl-full pointer-events-none"></div>

              <h2 className="text-3xl font-black text-gray-900 mb-10 tracking-tight relative z-10">
                Start Your Project
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-2">
                      Full Name
                    </label>
                    <input
                      required
                      type="text"
                      placeholder="e.g. Rahul Sharma"
                      className="w-full bg-gray-50/50 border border-gray-100 rounded-3xl px-6 py-4 outline-none focus:ring-4 focus:ring-blue-600/5 focus:bg-white focus:border-blue-600/30 transition-all font-bold placeholder:text-gray-300"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-2">
                      Company
                    </label>
                    <input
                      type="text"
                      placeholder="Your organization"
                      className="w-full bg-gray-50/50 border border-gray-100 rounded-3xl px-6 py-4 outline-none focus:ring-4 focus:ring-blue-600/5 focus:bg-white focus:border-blue-600/30 transition-all font-bold placeholder:text-gray-300"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-2">
                      Work Email
                    </label>
                    <input
                      required
                      type="email"
                      placeholder="rahul@company.com"
                      className="w-full bg-gray-50/50 border border-gray-100 rounded-3xl px-6 py-4 outline-none focus:ring-4 focus:ring-blue-600/5 focus:bg-white focus:border-blue-600/30 transition-all font-bold placeholder:text-gray-300"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-2">
                      Industry
                    </label>
                    <select className="w-full bg-gray-50/50 border border-gray-100 rounded-3xl px-6 py-4 outline-none focus:ring-4 focus:ring-blue-600/5 focus:bg-white focus:border-blue-600/30 transition-all font-bold text-gray-900 appearance-none cursor-pointer">
                      <option>Pharmaceutical</option>
                      <option>Food & Beverage</option>
                      <option>Cosmetics</option>
                      <option>FMCG & Retail</option>
                      <option>Logistics</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-2">
                    Requirements
                  </label>
                  <textarea
                    rows="4"
                    placeholder="Briefly describe your size, material, or volume requirements..."
                    className="w-full bg-gray-50/50 border border-gray-100 rounded-[32px] px-6 py-5 outline-none focus:ring-4 focus:ring-blue-600/5 focus:bg-white focus:border-blue-600/30 transition-all font-bold placeholder:text-gray-300 resize-none"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full bg-blue-600 text-white font-black py-6 rounded-3xl hover:bg-blue-700 transition-all shadow-[0_20px_40px_rgba(37,99,235,0.25)] hover:shadow-[0_25px_50px_rgba(37,99,235,0.4)] active:scale-[0.98] uppercase tracking-[0.2em] text-sm"
                >
                  Send Inquiry Now
                </button>

                {submitted && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-5 bg-green-50 border border-green-100 rounded-2xl text-green-700 text-center font-bold text-sm"
                  >
                    🚀 Great! Our specialists will contact you within 2 hours.
                  </motion.div>
                )}
              </form>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
    </>
  );
};

export default ContactUs;
